// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// "신규 케이스 등록" modal launched from the region dashboard. Uses the
// existing /visits/candidates endpoint to populate the elderly picklist.
// for_case=1로 호출해 두 부류를 후보에서 제외한다: ①승인관리에서 아직 승인대기
// (pending/reviewing)인 케어신청의 어르신, ②이미 진행 중(미종결) 케이스가 있는 어르신
// (cases#create의 409 open_case_exists와 동일 기준). 신청기록·진행 케이스가 없는 기존
// 돌봄 어르신은 그대로 노출.
//
// 등록은 기본적으로 "matching"으로 — 곧장 AI 자동매칭(COD) 큐에 올리고 백엔드가
// 후보 매니저를 자동 생성한다. "applying"(보류)은 아직 매칭을 미루고 싶을 때만
// 고르는 예외 선택지다. 이후 단계(assigned / caring / closed)는 별도 부수효과가
// 필요해 이 모달에서 다루지 않는다.

const EMPTY = { elderly_id: "", priority: "normal", status: "matching", note: "", service_type: "" };

const PRIORITY_OPTIONS = [
  { v: "critical", l: "높음" },
  { v: "warning",  l: "보통" },
  { v: "normal",   l: "낮음" },
];
const STATUS_OPTIONS = [
  { v: "matching",   l: "매칭 (AI 자동매칭에 바로 등록)" },
  { v: "applying",   l: "보류 (나중에 매칭)" },
];
const RISK_LABEL = { critical: "위험", warning: "주의", stable: "안정" };

// 그룹키 → UI 라벨. Setting::CARE_SERVICE_GROUP_LABELS 와 동일 소스. 백엔드 상수와
// 별도 상수로 두는 이유: 이 모달은 /settings API 로 카탈로그(그룹키+아이템)만 받고
// 그룹 표시 라벨은 프런트가 결정한다(운영자가 아이템 라벨은 바꿔도 그룹 이름은 유지).
// 새 그룹키가 추가되면 여기에 라벨을 심어야 optgroup 헤더가 뜬다 — 없으면 그룹키
// 원본 문자열을 그대로 노출해서 조용히 소실되진 않게 한다.
const SERVICE_GROUP_LABEL = {
  ltc:       "장기요양서비스",
  non_ltc:   "비장기요양서비스",
  emergency: "병원(응급상황)",
};

export function CaseCreateModal({ onClose, onCreated }) {
  useEscapeKey(onClose);
  const [form, setForm] = React.useState(EMPTY);
  const [elderlies, setElderlies] = React.useState([]);
  const [loadingPick, setLoadingPick] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);
  // 서비스 종류 카탈로그 — /api/v1/settings 의 general.care_service_types(JSON) 에서 로드.
  // 실패해도 폼 등록 자체는 계속 가능(서비스 종류는 선택 필드 — 김해 실무진 조정 기간
  // 동안 미기입 케이스가 있을 수 있음). 로드 실패 시 빈 목록으로 조용히 유지.
  const [serviceCatalog, setServiceCatalog] = React.useState({});
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  React.useEffect(() => {
    fetch("/api/v1/visits/candidates?for_case=1", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : { elderlies: [] })
      .then(j => setElderlies(j.elderlies || []))
      .catch(() => { /* surfaces as empty list — user retries */ })
      .finally(() => setLoadingPick(false));
  }, []);

  React.useEffect(() => {
    fetch("/api/v1/settings", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : {})
      .then(j => {
        const raw = j?.general?.care_service_types?.value;
        if (raw && typeof raw === "object") setServiceCatalog(raw);
      })
      .catch(() => { /* 서비스 종류는 선택 필드 — 실패해도 저장은 가능 */ });
  }, []);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    if (!form.elderly_id) return setErr("어르신을 선택해주세요.");
    setSubmitting(true);
    try {
      const r = await fetch("/api/v1/cases", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ case: form }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 401)      setErr("로그인이 필요합니다.");
        else if (r.status === 403) setErr(j.message || "케이스 등록 권한이 없습니다.");
        else if (r.status === 409) setErr(j.message || "이미 진행 중인 케이스가 있습니다.");
        else                       setErr(apiErrorMessage(j, r.status));
        return;
      }
      onCreated?.(j);
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setSubmitting(false); }
  };

  const selectedElderly = elderlies.find(e => String(e.id) === String(form.elderly_id));

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card" style={{width:560, padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">신규 케이스 등록</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>진행 중 케이스·승인대기 어르신 제외 · 등록 후 매칭 단계로 진행됩니다</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}

        <div style={{padding:22, display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
          <div style={{gridColumn:'1 / span 2'}}>
            <div className="label">어르신 *</div>
            <select className="select" value={form.elderly_id} onChange={e=>set('elderly_id')(e.target.value)} required disabled={loadingPick}>
              <option value="">{loadingPick ? "불러오는 중…" : "어르신을 선택하세요"}</option>
              {elderlies.map(e => (
                <option key={e.id} value={e.id}>
                  {e.name} · {e.age}세 · {RISK_LABEL[e.risk_level] || e.risk_level}
                </option>
              ))}
            </select>
            {selectedElderly && (
              <div className="muted" style={{fontSize:11, marginTop:6}}>{selectedElderly.address}</div>
            )}
            {!loadingPick && elderlies.length === 0 && (
              <div className="muted" style={{fontSize:11, marginTop:6}}>
                등록 가능한 어르신이 없습니다. 진행 중 케이스가 있거나 승인대기 중인 어르신은 제외됩니다.
              </div>
            )}
          </div>

          <div>
            <div className="label">우선순위</div>
            <select className="select" value={form.priority} onChange={e=>set('priority')(e.target.value)}>
              {PRIORITY_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div>
            <div className="label">초기 상태</div>
            <select className="select" value={form.status} onChange={e=>set('status')(e.target.value)}>
              {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>

          <div style={{gridColumn:'1 / span 2'}}>
            <div className="label">서비스 종류</div>
            <select className="select" value={form.service_type} onChange={e=>set('service_type')(e.target.value)}>
              <option value="">선택 안 함(추후 지정)</option>
              {Object.entries(serviceCatalog).map(([gk, items]) => (
                <optgroup key={gk} label={SERVICE_GROUP_LABEL[gk] || gk}>
                  {(Array.isArray(items) ? items : []).map(it => (
                    <option key={it.code} value={it.code}>{it.code} · {it.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <div className="muted" style={{fontSize:11, marginTop:6}}>
              장기요양(LTC) · 비장기요양(NLT) · 병원응급(ER) 3개 그룹. 목록은 시스템 설정에서 편집.
            </div>
          </div>

          <div style={{gridColumn:'1 / span 2'}}>
            <div className="label">메모</div>
            <textarea className="input" value={form.note} onChange={e=>set('note')(e.target.value)}
              rows={3} placeholder="예: 보호자 요청 사항, 평가 시 유의 사항"
              style={{resize:'vertical', minHeight:60}}/>
          </div>
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>취소</button>
          <button type="submit" className="btn btn-primary btn-md" disabled={submitting || loadingPick}>
            {submitting ? '등록 중…' : '케이스 등록'}
          </button>
        </div>
      </form>
    </div>
  );
}
